import { i18n } from '@boot/i18n';
import Compressor from 'compressorjs';
import { axios } from '@boot/axios.js';

export const MAX_MEDIA_COUNT = 5;

/**
 * Updates the media associated with the given phase.

 * This function takes in an array of `File` objects to upload as new media,
 * and an array of existing media IDs to remove. It will upload the new media
 * and remove the specified media IDs from the phase.
 *
 * @param {File[]} files The new media files to upload
 * @param {number[]} mediaIdsToRemove The IDs of media to remove from the phase
 * @param {number} phaseId The ID of the phase to update
 * @returns {Promise<void>} A promise that resolves when the media has been updated
 */
export async function updatePhaseMedia(files, mediaIdsToRemove, phaseId) {
    try {
        const formData = new FormData();
        if (files.length > 0) {
            appendFilesToFormData({
                files,
                formData,
                contextName: `phase_${phaseId}`,
                appendName: 'to_add'
            });
        }

        // Add media IDs to remove if provided
        if (mediaIdsToRemove && mediaIdsToRemove.length > 0) {
            for (const id of mediaIdsToRemove) {
                formData.append('to_remove', id);
            }
        }

        const response = await axios.post(
            `assetphases/${phaseId}/manage-media/`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        return response;
    } catch (error) {
        console.error('Failed to upload media:', error);
        // Let the component handle the error
        throw error;
    }
}

/**
 * Uploads media to justify an event.
 *
 * This function takes in an array of `File` objects to upload as justification
 * media, and a payload object with the remaining justification data. It will
 * upload the media and send the justification data to the server.
 *
 * @param {File[]} files The justification media files to upload
 * @param {Object} payload The remaining justification data
 * @returns {Promise<Object>} A promise that resolves with the server response
 */
export async function justifyEventWithMedia(files, payload) {
    try {
        const formData = new FormData();
        if (files.length > 0) {
            appendFilesToFormData({
                files,
                formData,
                contextName: 'justify',
                appendName: 'media'
            });
        }
        // Add payload to formData
        Object.keys(payload).forEach(key => {
            formData.append(key, payload[key]);
        });
        const response = await axios.post('events/resolve/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    } catch (error) {
        console.error('Failed to justify event with media:', error);
        // Let the component handle the error
        throw error;
    }
}

/**
 * Appends files to a FormData instance, renaming each file and allowing the appended property name to be customized.
 *
 * @param {Object} params - Parameters for appending files.
 * @param {File[]} params.files - The files to append.
 * @param {FormData} params.formData - The FormData instance to append to.
 * @param {string} params.contextName - The context name for filename context.
 * @param {string} params.appendName - The FormData property name to use (e.g., 'to_add').
 * @returns {void}
 */
export function appendFilesToFormData({
    files,
    formData,
    contextName,
    appendName
}) {
    files.forEach(file => {
        // Determine the new filename - you can customize this logic
        const newFilename = `media_event_${contextName}_${Date.now()}.${file.name.split('.').pop()}`;
        // The image file is processed by the MediaInputManager, check the compressImage method for details.
        const uploadFile = new File([file], newFilename, {
            type: file.type || 'application/octet-stream',
            lastModified: file.lastModified || Date.now()
        });
        formData.append(appendName, uploadFile);
    });
}

/**
 * Loads the media associated with the given phase.
 *
 * @param {number} phaseId The ID of the phase to load media from
 * @returns {Promise<Object[]>} A promise that resolves with an array of media objects.
 *     Each media object will have the following properties:
 *     - id: The ID of the media
 *     - url: The URL of the media file
 */
export async function loadPhaseMedia(phaseId) {
    try {
        const response = await axios.get(`media/`, {
            params: { phase: phaseId }
        });
        return response.data.results.map(media => ({
            id: media.id,
            url: media.file
        }));
    } catch (error) {
        console.error('Failed to fetch media:', error);
        // Let the component handle the error
        throw error;
    }
}

/**
 * Compresses the image to reduce file size and convert it to JPEG format.
 * @param {File} file - The image file to compress.
 * @returns {Promise} - Promise that resolves with the compressed image.
 */
export function compressImage(file) {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.8,
            // Compress the image
            maxWidth: 1200,
            maxHeight: 1200,
            // Convert the image to jpeg
            mimeType: 'image/jpeg',
            success: compressedImage => {
                resolve(compressedImage);
            },
            error: error => {
                reject(error);
            }
        });
    });
}

/**
 * Validates an array of image files against count, size, and type constraints.
 * @param {File[]} files - Array of file objects to validate.
 * @returns {Object} Validation result.
 * @returns {boolean} return.isValid - True if all files are valid, false otherwise.
 * @returns {string[]} return.errors - Array of error messages for failed validations.
 */
export function validateImage(files) {
    const errors = [];
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const allowedExts = ['.jpg', '.jpeg', '.png'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (files.length > 5) {
        errors.push(
            i18n.global.t(
                'The maximum number of media ({max}) has been reached.',
                {
                    max: MAX_MEDIA_COUNT
                }
            )
        );
    }
    files.forEach(file => {
        // Check type by MIME and extension
        const typeOk =
            allowedTypes.includes(file.type.toLowerCase()) ||
            allowedExts.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!typeOk) {
            errors.push(
                i18n.global.t(
                    `File {fileName} is not a supported image type (JPG, JPEG, PNG).`,
                    { fileName: file.name }
                )
            );
        }
        if (file.size > maxSize) {
            errors.push(
                i18n.global.t(`File {fileName} exceeds 5MB size limit.`, {
                    fileName: file.name
                })
            );
        }
    });
    return {
        isValid: errors.length === 0,
        errors
    };
}
